import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../supabase';
import pwsLogo from "../../../assets/logo800.webp";
import ImageWithSkeleton from "../../../components/ui/ImageWithSkeleton";
import { isPasswordStrongEnough } from "../../../utils/passwordStrength";
import './ForgotPassword.css';

// Custom-branded landing page for Supabase Auth action links (password reset,
// email verification). Unlike Firebase (a mode/oobCode pair that still needs
// a separate verify/confirm call), Supabase's link already completes
// verification server-side and redirects here with a full session in the URL
// hash (#access_token=...&type=recovery|signup). detectSessionInUrl is off on
// the shared client (supabase.js) specifically so this page can read `type`
// itself before anything strips the hash - this page calls setSession()
// manually for the recovery case instead.
export default function AuthAction() {
  const { t } = useTranslation('authAction');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const continueUrl = searchParams.get('continueUrl') || '/login';

  const [status, setStatus] = useState('loading'); // loading | ready | success | verified | error
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // StrictMode double-invokes effects in dev - the refresh token in the hash
  // is single-use, so a second setSession() call with the same one would fail.
  const sessionSetupStarted = useRef(false);

  useEffect(() => {
    if (sessionSetupStarted.current) return;
    sessionSetupStarted.current = true;

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const type = hashParams.get('type');

    if (!type) {
      setStatus('error');
      setError(t('invalidOrMissingLink'));
      return;
    }

    if (type === 'signup') {
      // Landing here at all means Supabase already confirmed the email server-
      // side (that's what the link's token did) - no further call needed.
      setStatus('verified');
      return;
    }

    if (type === 'recovery') {
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      if (!accessToken || !refreshToken) {
        setStatus('error');
        setError(t('linkInvalidOrExpired'));
        return;
      }

      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ data, error: sessionError }) => {
          if (sessionError || !data.session) {
            setStatus('error');
            setError(t('linkInvalidOrExpired'));
            return;
          }
          setEmail(data.session.user.email);
          setStatus('ready');
        });
      return;
    }

    setStatus('error');
    setError(t('unknownAction'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordStrongEnough(password)) {
      setError(t('passwordTooWeak'));
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      await supabase.auth.signOut();
      setStatus('success');
    } catch (err) {
      setError(t('couldNotResetPassword'));
    }
    setSubmitting(false);
  };

  return (
    <div className="login-wrapper">
      <div className="login-content">
        <div className="login-card">
          <div className="login-form-new">
            <div className="login-form-header">
              <div className="login-logo-wrap">
                <ImageWithSkeleton
                  src={pwsLogo}
                  alt="PLAYWORLD"
                  loading="eager"
                  imgStyle={{ width: "100%", height: "auto", display: "block" }}
                  style={{ width: "100%", zIndex: 2, position: "relative" }}
                />
              </div>

              {status === 'loading' && <div className="login-card-title">{t('checkingLink')}</div>}

              {status === 'ready' && (
                <>
                  <div className="login-card-title">{t('resetPassword')}</div>
                  <div className="login-card-subtitle">{t('forEmail', { email })}</div>
                </>
              )}

              {status === 'success' && <div className="login-card-title">{t('passwordUpdated')}</div>}
              {status === 'verified' && <div className="login-card-title">{t('emailConfirmed')}</div>}
              {status === 'error' && <div className="login-card-title">{t('somethingWentWrong')}</div>}
            </div>

            {error && <div className="login-error-message">{error}</div>}

            {status === 'ready' && (
              <form className="login-form-new" onSubmit={handleSubmit}>
                <div className="login-field">
                  <label>{t('newPassword')}</label>
                  <input
                    type="password"
                    placeholder={t('newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button className="login-btn-submit" type="submit" disabled={submitting}>
                  {submitting ? t('saving') : t('saveNewPassword')}
                </button>
              </form>
            )}

            {(status === 'success' || status === 'verified' || status === 'error') && (
              <button
                className="login-btn-submit"
                onClick={() => {
                  // continueUrl is a full absolute URL (Supabase's redirectTo
                  // requires one) - react-router's navigate() only understands
                  // in-app relative paths, so a real URL has to go through
                  // window.location instead or it gets treated as a path
                  // segment appended to /auth-action.
                  if (/^https?:\/\//i.test(continueUrl)) {
                    window.location.href = continueUrl;
                  } else {
                    navigate(continueUrl);
                  }
                }}
              >
                {status === 'error' ? t('backToLogin') : t('continueToLogin')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
